import React, { useState, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Position } from '@/hooks/usePositions';

interface PositionComboboxProps {
    selectedId: string | undefined;
    onChange: (position: Position | null) => void;
    disabled?: boolean;
    positions: Position[];
    loading: boolean;
    error: string | null;
}

const PositionCombobox: React.FC<PositionComboboxProps> = ({ selectedId, onChange, disabled, positions, loading, error }) => {
    const [query, setQuery] = useState('');

    const selectedPosition = positions.find(p => p.id === selectedId);

    const filteredPositions =
        query === ''
            ? positions
            : positions.filter((position) =>
                position.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            );

    if (loading) return <div className="input-primary w-full mt-1 text-gray-500">Carregando cargos...</div>;
    if (error) return <div className="input-primary w-full mt-1 text-red-500">{error}</div>;

    return (
        <Combobox value={selectedPosition || null} onChange={onChange} disabled={disabled}>
            <div className="relative mt-1">
                <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm border border-gray-300">
                    <Combobox.Input
                        className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                        displayValue={(position: Position) => position?.name || ''}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Selecione um cargo"
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                        />
                    </Combobox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
                        {filteredPositions.length === 0 && query !== '' ? (
                            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                Nenhum cargo encontrado.
                            </div>
                        ) : (
                            filteredPositions.map((position) => (
                                <Combobox.Option
                                    key={position.id}
                                    className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                    }
                                    value={position}
                                >
                                    {({ selected, active }) => (
                                        <>
                                            <span
                                                className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                                    }`}
                                            >
                                                {position.name}
                                            </span>
                                            {selected ? (
                                                <span
                                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'
                                                        }`}
                                                >
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </Combobox.Option>
                            ))
                        )}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default PositionCombobox; 